import { Tabs } from './components/Tabs';
import { outputDataService, useGetOutputData } from '../../services/outputData.service copy';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetWorkflowById } from '../../services/workflow.services';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useGetWorklflowsPeriods } from '../../services/period.service';
import { Errors } from './components/Errors';
import {
  Box,
  ScrollArea,
  Flex,
  LoadingOverlay,
  Text,
  CloseButton,
  Overlay,
  Switch
} from '@mantine/core';
import CanvasTable from '../../components/CanvasTable';
import * as Styled from '../../components/CanvasTable/style';
import authStore from '../../store/auth';
import { useGetJurisdictions } from '../../services/jurisdiction.service';
import { useUserGroupsCurrentUserAdminState } from '../../services/user.service';
import { TbChevronDown, TbChevronUp } from 'react-icons/tb';
import { checkToRedirect } from '../../utils/checkToRedirect';
import agentStore from '../../store/agent';
import { WEB_PAGE_STATE_ENUMS } from '../../components/ChatWidget/constants';
import { SPECIAL_NULL_STRING } from '../../components/CanvasTable/constants';
import { REPORT_TYPES } from '../../consts/reportTypes';
import { permissionPolicies } from '../../permissions/policies';
import { useValidateDetailedOutputStats } from '../../services/funds.service';
import { notifications } from '@mantine/notifications';
import { extractApiError } from '../../utils';
import { useWorkflowChrome } from '../../components/Layout/WorkflowChromeContext';
import { USER_ROLES } from '../../consts/userRoles';
import { useSignalRListener } from '../../hooks/useSignalR';
import { SIGNALR_LISTENERS } from '../../config/signalr';
import { idCreator } from '../Project/utils/idCreator';
import OutputCanvasTable from './components/OutputCanvasTable';

function generateAlphabetColumn(columnNumber) {
  let column = '';
  while (columnNumber > 0) {
    const remainder = (columnNumber - 1) % 26;
    column = String.fromCharCode(65 + remainder) + column;
    columnNumber = Math.floor((columnNumber - 1) / 26);
  }

  return column;
}

const noop = () => {};

const getErrorType = val => {
  if (val === 'Warning') return 'warnings';
  else if (val === 'Error') return 'errors';

  return 'mandatory_fixes';
};

// Error / warning / mandatory-fix highlight colours. CanvasTable paints cell
// backgrounds and borders directly onto the canvas via the 2D context, which
// can't resolve CSS custom properties — so the concrete values from
// `--error-bg` etc. (index.css) and the mantine red/orange/gray-8 shades used
// for the dashed column frame are inlined here.
const HIGHLIGHT_COLORS = {
  error: '#fff5f5',
  mandatory: '#fff4e6',
  warning: '#ffe29f5b'
};

const BORDER_COLORS = {
  error: '#e03131',
  mandatory: '#e8590c',
  warning: '#343a40'
};

export default function OutputTables() {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const [includeCustomField, setIncludeCustomField] = useState(false);
  const [selectedWarning, setSelectedWarning] = useState(null);
  const [selectedMandatoryIssue, setSelectedMandatoryIssue] = useState(null);
  const [selectedError, setSelectedError] = useState(null);
  const [focusLocation, setFocusLocation] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [table, setTable] = useState(null);
  const [isOpenSidebar, setIsOpenSidebar] = useState(true);
  const previousIncludeCustomFieldRef = useRef(false);
  const { projectId, periodId } = useParams();
  const [isValidatingInitialOutput, setIsValidatingInitialOutput] = useState(false);

  const initialParams = searchParams.get('initial_params');

  const clearInitialParams = () =>
    setSearchParams(init => {
      init.delete('initial_params');
      return init;
    });

  const workflowsPeriods = useGetWorklflowsPeriods({
    periodId: periodId,
    queryOptions: {
      enabled: !!periodId
    }
  });

  const {
    data,
    isLoading,
    refetch: refetchOutputData
  } = useGetOutputData({
    headers: {
      'Accept-Encoding': 'gzip'
    },
    params: {
      period_id: periodId,
      add_custom_fields: includeCustomField
    },
    queryOptions: {
      // Hold the fetch until periodId is parsed from the URL — otherwise the
      // heavy /api/get-data call fires with `period_id=undefined`, burning a
      // request and the loading spinner before the route params resolve.
      enabled: !!periodId,
      onSuccess: res => {
        if (!initialParams && !selectedCategory) {
          setTable(res?.[0]?.tables?.[0]);
        } else if (initialParams && !selectedCategory) {
          const _category = initialParams.split('_')[1];
          const foundCategory = res.find(category => category.name === _category);
          setSelectedCategory(foundCategory);
          setTable(foundCategory?.tables?.[0]);
        } else if (previousIncludeCustomFieldRef.current !== includeCustomField) {
          if (table && selectedCategory) {
            const tableData = res
              .find(category => category.name === selectedCategory?.name)
              ?.tables?.find(item => item.name === table?.name);
            setTable(tableData);
          } else {
            setTable(res?.[0]?.tables?.[0]);
          }
        }

        return res;
      }
    }
  });

  const { data: project } = useGetWorkflowById({
    id: projectId,
    queryParams: {
      enabled: true,
      onSuccess: res => {
        authStore.setWorkflow(res.id, res.name);
      }
    }
  });

  const { data: jurisdictions } = useGetJurisdictions({
    periodId,
    queryParams: {
      enabled: !!periodId
    }
  });

  const { data: currentUser } = useUserGroupsCurrentUserAdminState();

  // Same pattern the extraction page uses: when the backend fires an output
  // table SignalR event for this period, refetch the heavy /api/get-data
  // payload directly instead of waiting on the chrome's period query to
  // invalidate and then chasing follow-up fetches. Subscribing both the
  // output-table event and the workflow-period event keeps the grid in sync
  // when output_table_id is missing from the payload too.
  const handleOutputTableStatusUpdate = useCallback(
    data => {
      if (data?.period_id && data.period_id !== periodId) return;
      refetchOutputData();
    },
    [periodId, refetchOutputData]
  );

  useSignalRListener(
    SIGNALR_LISTENERS.ON_OUTPUT_TABLE_STATUS_UPDATED,
    handleOutputTableStatusUpdate
  );
  useSignalRListener(SIGNALR_LISTENERS.ON_WORKFLOW_PERIOD_UPDATED, handleOutputTableStatusUpdate);

  const validateDetailedOutputStats = useValidateDetailedOutputStats();

  const scrolledPosition = useRef({ top: 0, left: 0 });
  const onChangeScrollPosition = useCallback(e => {
    scrolledPosition.current = { top: e?.top ?? 0, left: e?.left ?? 0 };
  }, []);

  const isApprover = currentUser?.role === USER_ROLES.APPROVER;

  const canExportAsXlsx =
    permissionPolicies.checkAccess({
      currentUserRole: currentUser?.role,
      currentUserId: currentUser?.azure_object_id,
      assignedTo: project?.assigned_to
    }) || isApprover;

  const findAndFocusToCell = useCallback(
    value => {
      let rowIndex, columnIndex;

      table?.data?.forEach((_row, _rowIndex) => {
        if (rowIndex && columnIndex) {
          return { rowIndex, columnIndex };
        }
        const cols = Object.values(_row);

        cols.forEach((_col, _colIndex) => {
          if (value === _col) {
            rowIndex = _rowIndex + 1;
            columnIndex = _colIndex + 1;

            return { rowIndex: _rowIndex, columnIndex: _colIndex };
          }
        });
      });

      return { rowIndex, columnIndex };
    },
    [table?.data]
  );

  const onSelectWarning = useCallback(
    ({ table, category, warning, type, field }) => {
      setSelectedError();
      setSelectedMandatoryIssue();
      setSelectedWarning({ table, category, warning, type, field });
      const location = findAndFocusToCell(warning?.field);

      if (warning?.rows) {
        setFocusLocation({
          ...location,
          rowIndex: warning.rows[0]
        });
        return;
      }
      setFocusLocation(location);
      clearInitialParams();
    },
    [findAndFocusToCell]
  );

  const onSelectMandatoryIssue = useCallback(
    ({ table, category, error, type, field }) => {
      setSelectedError();
      setSelectedWarning();

      setSelectedMandatoryIssue({ table, category, error, type, field });
      const location = findAndFocusToCell(error?.field);

      if (error?.rows) {
        setFocusLocation({
          ...location,
          rowIndex: error.rows[0]
        });
        return;
      }
      setFocusLocation(location);
      clearInitialParams();
    },
    [findAndFocusToCell]
  );

  const onSelectError = useCallback(
    ({ table, category, error, type, field }) => {
      setSelectedWarning();
      setSelectedMandatoryIssue();

      setSelectedError({ table, category, error, type, field });
      const location = findAndFocusToCell(error?.field);
      if (error?.rows) {
        setFocusLocation({
          ...location,
          rowIndex: error.rows[0]
        });
        return;
      }
      setFocusLocation(location);
      clearInitialParams();
    },
    [findAndFocusToCell]
  );

  useEffect(() => {
    if (!data || !initialParams) return;

    const setInitialParams = () => {
      const _table = initialParams.split('_')[0];
      const _category = initialParams.split('_')[1];
      const _errorType = getErrorType(initialParams.split('_')[2]);
      const _field = initialParams.split('_')[3];

      const foundCategory = data.find(category => category.name === _category);
      const foundElement = foundCategory?.tables
        .find(table => table.name === _table)
        ?.[_errorType]?.find(type => type.field === _field);

      if (_errorType === 'errors')
        return onSelectError({
          table: _table,
          category: _category,
          error: foundElement,
          type: 'error',
          field: _field
        });
      else if (_errorType === 'warnings')
        return onSelectWarning({
          table: _table,
          category: _category,
          warning: foundElement,
          type: 'warning',
          field: _field
        });

      return onSelectMandatoryIssue({
        table: _table,
        category: _category,
        error: foundElement,
        type: 'mandatory',
        field: _field
      });
    };

    setInitialParams();
  }, [initialParams, data, onSelectError, onSelectWarning, onSelectMandatoryIssue]);

  const rowCount = useMemo(() => {
    if (!data || !data?.[0]) return 0;

    return Math.round((window.innerWidth - 460) / 100) - Object.keys(data?.[0])?.length > 0
      ? Math.round((window.innerWidth - 460) / 100) - Object.keys(data?.[0]).length
      : 0;
  }, [data]);

  // canvas-datagrid derives its column headers from each row object's keys and
  // renders the row-number gutter itself, so we key the values by the alphabet
  // labels (A, B, C…) that used to be TanstackTable's column headers and drop
  // the manual № column.
  const _data = useMemo(() => {
    return table?.data?.map(item => {
      const newItem = {};

      Object.values(item).forEach((value, i) => {
        newItem[generateAlphabetColumn(i + 1)] = value ?? SPECIAL_NULL_STRING;
      });

      return newItem;
    });
  }, [table?.data]);

  // CanvasTable calls getCellStyle(cell.rowIndex + 2, cell.columnIndex + 1) per
  // cell while painting the canvas, so selecting an error/warning/mandatory
  // issue only needs a repaint — no per-cell React re-render. The whole column
  // of the selected issue (`focusLocation.columnIndex`) gets a dashed frame and
  // the matching tint; the found row is additionally boxed by the native active
  // cell (via `foundCell`). Everything else returns undefined (default).
  const getCellStyle = useCallback(
    (rowIndex, columnIndex) => {
      if (focusLocation?.columnIndex == null) return undefined;
      if (columnIndex !== focusLocation.columnIndex) return undefined;

      const type = selectedError?.type || selectedMandatoryIssue?.type || selectedWarning?.type;
      if (!type) return undefined;

      return {
        borderColor: BORDER_COLORS[type],
        background: HIGHLIGHT_COLORS[type]
      };
    },
    [focusLocation, selectedError, selectedMandatoryIssue, selectedWarning]
  );

  const allIssues = useMemo(() => {
    if (!data) return [];

    return [
      ...data
        .map(category => ({
          category: category.name,
          tables: [
            ...category.tables.map(table => ({
              name: table.name,
              type: 'error',
              errors: [
                ...table.errors.map(error => ({
                  ...error,
                  dataType: 'error',
                  count: error.rows?.length
                }))
              ]
            })),
            ...category.tables.map(table => ({
              name: table.name,
              type: 'mandatory',
              errors: [
                ...table.mandatory_fixes.map(error => ({
                  ...error,
                  dataType: 'mandatory',
                  count: error.rows?.length
                }))
              ]
            })),
            ...category.tables.map(table => ({
              name: table.name,
              type: 'warning',
              warnings: [
                ...table.warnings.map(warning => ({
                  ...warning,
                  dataType: 'warning',
                  count: warning.rows?.length
                }))
              ]
            }))
          ]
        }))
        .filter(
          category =>
            category.tables.filter(table => table.errors?.length > 0 || table.warnings?.length > 0)
              ?.length > 0
        )
    ];
  }, [data]);

  const isCustomReportType = project?.report_type === REPORT_TYPES.CUSTOM;

  const onChangeTableOnClick = value => {
    setFocusLocation({});
    setSelectedWarning(null);
    setSelectedError(null);
    setSelectedMandatoryIssue(null);
    setTable(value || table);
    clearInitialParams();
  };

  const onRedirectToReportPage = async () => {
    if (project?.report_type === REPORT_TYPES.FORM_PF) {
      navigate(`/report/pf/us/${projectId}/${periodId}`);
      return;
    }

    navigate(
      `/report/${jurisdictions[0].reporting_member_state === 'UK' ? 'fca' : 'esma'}/${jurisdictions[0].reporting_member_state.toLowerCase()}/${projectId}/${periodId}`
    );
  };

  const onNext = () => {
    if (
      workflowsPeriods.data?.template_type === 'from template' &&
      workflowsPeriods.data?.report_progress_status === 'Not started'
    ) {
      return navigate(checkToRedirect(project));
    }

    onRedirectToReportPage();
  };

  const isInitialPeriod = workflowsPeriods.data?.template_type === 'sratch';

  const hasOutputTableErrors = categories =>
    categories?.some(category => category?.tables?.some(table => table?.errors?.length > 0));

  const onValidateAndNext = () => {
    if (isInitialPeriod) {
      setIsValidatingInitialOutput(true);

      outputDataService
        .getOutputData({
          period_id: periodId
        })
        .then(outputData => {
          if (hasOutputTableErrors(outputData)) {
            notifications.show({
              color: 'red',
              title:
                'Your recent changes caused error in output table, please fix it before generating the report.',
              position: 'top-center',
              withCloseButton: false
            });
            return;
          }

          onNext();
        })
        .catch(err => {
          notifications.show({
            color: 'red',
            title: extractApiError(
              err,
              'Unable to validate output data before generating the report.'
            ),
            position: 'top-center',
            withCloseButton: false
          });
        })
        .finally(() => {
          setIsValidatingInitialOutput(false);
        });

      return;
    }

    validateDetailedOutputStats.mutate(
      { periodId: periodId },
      {
        onSuccess: stats => {
          if (stats?.errors?.length > 0) {
            notifications.show({
              color: 'red',
              title:
                'Your recent changes caused error in output table, please fix it before generating the report.',
              position: 'top-center',
              withCloseButton: false
            });
            return;
          }
          onNext();
        },
        onError: err => {
          notifications.show({
            color: 'red',
            title: extractApiError(
              err,
              'Unable to validate output data before generating the report.'
            ),
            position: 'top-center',
            withCloseButton: false
          });
        }
      }
    );
  };

  const onDownloadFile = async () => {
    if (!canExportAsXlsx) return;

    const token = authStore.token;

    setIsDownloading(true);

    try {
      const endpoint = isApprover
        ? `api/create-excel-file-approver?period_id=${periodId}`
        : `api/create-excel-file?period_id=${periodId}`;

      const response = await fetch(`${import.meta.env.VITE_BASE_URL}${endpoint}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          workflow_id: authStore.workflowId
        }
      });
      if (!response.ok) {
        setIsDownloading(false);
        throw new Error('Network response was not ok');
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'output-tables.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch.length === 2) {
          filename = filenameMatch[1];
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', filename);

      document.body.appendChild(link);

      link.click();
      link.parentNode.removeChild(link);

      window.URL.revokeObjectURL(url);
      setIsDownloading(false);
    } catch (e) {
      console.log('errrr===>', e);
      setIsDownloading(false);
    }
  };

  const toggleSidebar = () => {
    setIsOpenSidebar(prev => !prev);
  };

  const onNavigateUp = () => {
    const rows =
      selectedMandatoryIssue?.error?.rows ||
      selectedError?.error?.rows ||
      selectedWarning?.warning?.rows;
    const findIndex = rows?.findIndex(value => value === focusLocation?.rowIndex);
    if (findIndex > 0) {
      setFocusLocation(prev => ({
        ...prev,
        rowIndex: rows[findIndex - 1]
      }));
    }
  };

  const onNavigateDown = () => {
    const rows =
      selectedMandatoryIssue?.error?.rows ||
      selectedError?.error?.rows ||
      selectedWarning?.warning?.rows;
    const findIndex = rows?.findIndex(value => value === focusLocation?.rowIndex);
    if (findIndex !== rows.length - 1) {
      setFocusLocation(prev => ({
        ...prev,
        rowIndex: rows[findIndex + 1]
      }));
    }
  };

  const renderSelectedIssue = issue => {
    const type = issue.type;
    const issueKey = `${idCreator(issue.table)}-${type}`;
    const color = type === 'error' ? '#E03131' : type === 'mandatory' ? '#FD7E14' : '#868E96';
    const text = type === 'error' ? 'error' : type === 'mandatory' ? 'issue' : 'warning';

    const rows = issue?.error?.rows || issue?.warning?.rows;
    const findIndex = rows?.findIndex(value => value === focusLocation?.rowIndex);
    return (
      <Flex align="center" gap="4px">
        <Text id={`output-table-selected-issue-table-${issueKey}`} fz="16px" lh="22px" c="dark.6">
          {issue.table}:
        </Text>
        <Flex gap={'8px'} align="center">
          <Text
            id={`output-table-selected-issue-summary-${issueKey}`}
            fz="16px"
            lh="22px"
            c={color}
          >
            {rows ? (
              <>
                {findIndex + 1}/{rows.length} {text}s selected
              </>
            ) : (
              <> 1 {text} selected</>
            )}
          </Text>
          {rows && (
            <Flex align="center" gap="8px">
              <CloseButton
                id="output-table-selected-issue-prev"
                onClick={onNavigateDown}
                c="gray.6"
                size="sm"
                icon={<TbChevronDown fontSize="20px" />}
              />
              <CloseButton
                id="output-table-selected-issue-next"
                onClick={onNavigateUp}
                size="sm"
                c="gray"
                icon={<TbChevronUp fontSize="20px" />}
              />
            </Flex>
          )}
          <CloseButton
            id="output-table-selected-issue-clear"
            c="gray"
            onClick={() => onChangeTableOnClick()}
            size="sm"
          />
        </Flex>
      </Flex>
    );
  };

  const issue = selectedError || selectedMandatoryIssue || selectedWarning;

  useEffect(() => {
    agentStore.setCurrentScreen(WEB_PAGE_STATE_ENUMS.OutputTable);
  }, []);

  const { setLabels, clearLabels, setToolbarSlots } = useWorkflowChrome();

  // Stable refs so the chrome useEffects don't re-fire each render (handlers
  // get a fresh closure every time).
  const onDownloadFileRef = useRef(onDownloadFile);
  const onNextRef = useRef(onNext);
  useEffect(() => {
    onDownloadFileRef.current = onDownloadFile;
    onNextRef.current = onNext;
  });

  useEffect(() => {
    setLabels({
      projectLabel: project?.name,
      periodLabel:
        workflowsPeriods.data?.year && workflowsPeriods.data?.period
          ? `${workflowsPeriods.data.year}, ${workflowsPeriods.data.period}`
          : undefined,
      workflowId: projectId,
      periodId,
      activeStageKey: 'output_data',
      // Stepper Next on this page runs the same `onNext` logic as the
      // (now-removed) inline "Next to Report" button — `onNext` knows how to
      // generate the report first when not yet started, then route to the
      // jurisdiction-specific report page. Custom workflows and Approvers
      // don't get the handler: Custom skips the Report stage entirely, and
      // Approvers are read-only and shouldn't be triggering generation.
      nextStepHandler: isCustomReportType || isApprover ? undefined : () => onNextRef.current?.(),
      // Approvers' stepper is filtered to [Output Data, Report] — no prevStage
      // exists for Output Data, which disables the ← button.
      backStepHandler: isApprover ? () => navigate('/project') : undefined
    });
    return () => clearLabels();
  }, [
    project?.name,
    workflowsPeriods.data?.year,
    workflowsPeriods.data?.period,
    projectId,
    periodId,
    isCustomReportType,
    isApprover,
    navigate,
    setLabels,
    clearLabels
  ]);

  useEffect(() => {
    setToolbarSlots(
      canExportAsXlsx
        ? {
            exportAction: {
              label: 'Export as XLSX',
              onClick: () => onDownloadFileRef.current?.(),
              loading: isDownloading,
              variant: 'ghost'
            }
          }
        : null
    );
    return () => setToolbarSlots(null);
  }, [canExportAsXlsx, isDownloading, setToolbarSlots]);

  return (
    <>
      <Box id="output-table-page" bg="gray.0" p="16px" h="calc(100vh - 149px)">
        <LoadingOverlay
          visible={isLoading}
          zIndex={1000}
          data-testid="loader"
          overlayProps={{ radius: 'sm', blur: 2 }}
        />
        <Box id="output-table-tabs-shell" pos="relative">
          {!!issue && <Overlay color="#fff" backgroundOpacity={0.7} />}
          <Tabs
            periodId={periodId}
            projectId={projectId}
            onChangeTable={onChangeTableOnClick}
            onChangeCategory={value => setSelectedCategory(value)}
            data={data}
            selectedCategory={selectedCategory}
            hideSourceList={isApprover}
          />
        </Box>
        <Flex gap="12px" mt="12px">
          {!isApprover && (
            <ScrollArea
              id="output-table-errors-sidebar"
              data-testid="errors-sidebar"
              scrollbarSize={14}
              scrollHideDelay={500}
              sx={() => ({
                borderRadius: '4px',
                border: '1px solid #F1F3F5',
                overflow: 'auto',
                flexDirection: 'column',
                gap: '24px',
                minWidth: isOpenSidebar ? '30%' : '60px',
                transition: '0.4s ease-in-out all'
              })}
              w={isOpenSidebar ? '30%' : '60px'}
              p="16px 12px"
              bg="#fff"
              h="calc(100vh - 317px)"
              display="flex"
            >
              <Errors
                selectedCategory={selectedCategory}
                data={allIssues}
                hasPanelBtn
                onSelectError={onSelectError}
                onSelectMandatoryIssue={onSelectMandatoryIssue}
                onSelectWarning={onSelectWarning}
                toggleSidebar={toggleSidebar}
                isOpenSidebar={isOpenSidebar}
                issue={issue}
              />
            </ScrollArea>
          )}

          <Box
            id="output-table-table-shell"
            sx={() => ({
              borderRadius: '4px',
              border: '1px solid #F1F3F5',
              maxWidth: isApprover ? '100%' : isOpenSidebar ? '70%' : 'calc(100% - 60px)'
            })}
            p="16px"
            bg="#fff"
            w={isApprover ? '100%' : isOpenSidebar ? 'calc(70% - 12px)' : 'calc(100% - 60px)'}
            className="table"
          >
            <Flex mb="16px" align="center" justify="space-between">
              {issue ? (
                renderSelectedIssue(issue)
              ) : (
                <Text id="output-table-source-table-label" fz="16px" lh="22px" c="dark.6">
                  Source Table
                </Text>
              )}
              <Switch
                id="output-table-show-custom-fields"
                label="Show Custom Fields"
                checked={includeCustomField}
                onChange={() => {
                  previousIncludeCustomFieldRef.current = includeCustomField;
                  setIncludeCustomField(prev => !prev);
                }}
              />
            </Flex>

            {!isLoading && _data && (
              <Box h="calc(100vh - 387px)">
                <OutputCanvasTable
                  data={_data}
                  foundCell={focusLocation}
                  zoomLevel={1}
                  getCellStyle={getCellStyle}
                  onFocusedCell={noop}
                  setSelectedCells={noop}
                  handleSelection={noop}
                  rowPinning={{ top: [] }}
                  columnPinning={{ left: [] }}
                  scrolledPosition={scrolledPosition.current}
                  onChangeScrollPosition={onChangeScrollPosition}
                />
              </Box>
            )}
          </Box>
        </Flex>
      </Box>
    </>
  );
}
